import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { track } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { PaymentFailedView } from './PaymentFailed.view';

/**
 * Payment failed, the container. Order-lock cleanup, analytics, redirect timer.
 *
 * Everything visual lives in `PaymentFailed.view.jsx`, which is pure, so the
 * state explorer can mount the real screen without any of this running.
 *
 * `PaymentFailed.css` is gone: nothing else imported it and the page no longer
 * emits one of its class names.
 */

/** Seconds on the auto-redirect readout, and on the timer it reads out. */
const REDIRECT_SECONDS = 5;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const PaymentFailed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [redirectIn, setRedirectIn] = useState(null);

  useEffect(() => {
    // Clear order lock on payment failure so user can try again
    const orderLockKey = `order_lock_${user?.id || user?._id || 'guest'}`;
    localStorage.removeItem(orderLockKey);
    const pending = JSON.parse(localStorage.getItem('pendingOrderInfo') || 'null');
    track(EVENTS.PAYMENT_FAILED, {
      order_id: pending?.orderId,
      failure_reason: 'gateway_redirect_failed',
    });

    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, REDIRECT_SECONDS * 1000);

    return () => clearTimeout(timer);
  }, [navigate, user]);

  // The readout for the timer above. It is a state announcement, never the way
  // out: the buttons always work. A number that changes every second is motion
  // too, so under `prefers-reduced-motion` it is simply not shown.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    setRedirectIn(REDIRECT_SECONDS);
    const ticker = setInterval(() => {
      setRedirectIn((seconds) => (seconds == null ? null : Math.max(0, seconds - 1)));
    }, 1000);
    return () => clearInterval(ticker);
  }, []);

  return <PaymentFailedView redirectIn={redirectIn} />;
};

export default PaymentFailed;
